import React, { Suspense, useEffect } from "react";
import type { ErrorInfo } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { reportClientError } from "./utils/report";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallBack from "./pages/error/ErrorFallBack";
import { useModal } from "./utils/useModal";
// import { usePreventContextMenu } from "./utils/usePreventContextMenu";

// ──────────────────────────────────────────────────────────────
// 2026-04-19 fix(#2): ChunkLoadError 자동 복구
// 배포 직후 유저 브라우저가 "옛 index.html(새 청크 참조 불가)"을 캐시하고
// 있을 때 발생. 10초 창 내 1회만 자동 새로고침하여 무한 루프를 방지.
// CI/CD 쪽(#1)에서 이미 원자적 배포로 막아두지만, CDN edge lag 등 극단적
// 케이스에서도 유저가 수동 재설치할 필요 없이 자동 회복되도록 방어벽.
// ──────────────────────────────────────────────────────────────
const CHUNK_RELOAD_KEY = "bc_chunk_reload_ts";
const CHUNK_RELOAD_WINDOW_MS = 10_000;

function isChunkLoadError(error: Error): boolean {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = error.message || "";
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /'text\/html'.*is not a valid JavaScript MIME type/i.test(msg)
  );
}

function handleErrorBoundaryError(error: Error, info: ErrorInfo) {
  if (isChunkLoadError(error)) {
    try {
      const lastAttempt = Number(
        window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0
      );
      const now = Date.now();
      if (now - lastAttempt > CHUNK_RELOAD_WINDOW_MS) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        window.location.reload();
        return; // 새로고침으로 페이지가 교체되므로 중복 알림 생략
      }
      // 10초 창 내 동일 에러 재발 → 진짜 문제이므로 보고 후 Fallback UI
    } catch {
      // sessionStorage 접근 불가(시크릿 모드 등) → 그냥 보고만 하고 Fallback
    }
  }
  reportClientError(error, info);
}

const loading = <></>;

// Containers
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));

// Dev-only QA Harness (non-production에서만 마운트)
const QaIndex = React.lazy(() => import("./pages/dev/qa"));
const QaGauge = React.lazy(() => import("./pages/dev/qa/gauge"));
const QaExpansion = React.lazy(() => import("./pages/dev/qa/expansion"));
const QaRedMasking = React.lazy(() => import("./pages/dev/qa/redmasking"));
const QaToasts = React.lazy(() => import("./pages/dev/qa/toasts"));
const QaDecks = React.lazy(() => import("./pages/dev/qa/decks"));
const QaSearchTTS = React.lazy(() => import("./pages/dev/qa/search-tts"));
const QaEditPublish = React.lazy(() => import("./pages/dev/qa/edit-publish"));
const QaCopyAndEdit = React.lazy(() => import("./pages/dev/qa/copy-and-edit"));
const QaChallengeRoom = React.lazy(
  () => import("./pages/dev/qa/challenge-room")
);
const QaLevelCeremony = React.lazy(
  () => import("./pages/dev/qa/level-ceremony")
);
const QA_ENABLED = process.env.REACT_APP_ENV !== "production";

// Dev-only C2C 챌린지룸 preview (handoffs/specs/challenge-room.spec.md §5).
const ChallengeRoomIndex = React.lazy(
  () => import("./pages/dev/challenge-room")
);
const ChallengeRoomMain = React.lazy(
  () => import("./pages/dev/challenge-room/main")
);
const ChallengeRoomDetail = React.lazy(
  () => import("./pages/dev/challenge-room/room")
);
const ChallengeRoomCreate = React.lazy(
  () => import("./pages/dev/challenge-room/create")
);
const ChallengeRoomCert = React.lazy(
  () => import("./pages/dev/challenge-room/cert")
);
const ChallengeRoomProGate = React.lazy(
  () => import("./pages/dev/challenge-room/pro-gate")
);

// Pages

//컴포넌트
const App = () => {
  const { openModal } = useModal();
  // usePreventContextMenu();

  useEffect(() => {
    /**
     * 네이티브 iOS 사진 권한 체크 함수
     */
    // const addPhoto = () => {
    //   try {
    //     window.webkit?.messageHandlers?.addPhoto.postMessage("");
    //   } catch (e: any) {
    //     console.log(e);
    //     alert(e.message);
    //   }
    // };

    const iOSAlert = (message: string) => {
      openModal({ type: "guide", message: message });
    };

    /**
     * FCM 토큰 받아와서 LocalStorage에 할당
     * @param token 저장할 토큰 값
     */
    const getFcmToken = (token: string) => {
      if (!token) return;
      localStorage.setItem("fcmToken", token);
    };

    window.requestIOSError = iOSAlert;
    window.getFcmToken = getFcmToken;
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary
        key={location.pathname}
        FallbackComponent={ErrorFallBack}
        onError={handleErrorBoundaryError}
      >
        <Suspense fallback={loading}>
          <Routes>
            {QA_ENABLED && (
              <Route path="/dev/qa" element={<QaIndex />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/gauge" element={<QaGauge />} />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/qa/level-ceremony"
                element={<QaLevelCeremony />}
              />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/expansion" element={<QaExpansion />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/red-masking" element={<QaRedMasking />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/toasts" element={<QaToasts />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/decks" element={<QaDecks />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/search-tts" element={<QaSearchTTS />} />
            )}
            {QA_ENABLED && (
              <Route path="/dev/qa/edit-publish" element={<QaEditPublish />} />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/qa/copy-and-edit"
                element={<QaCopyAndEdit />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/qa/challenge-room"
                element={<QaChallengeRoom />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room"
                element={<ChallengeRoomIndex />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room/main"
                element={<ChallengeRoomMain />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room/room/:roomId"
                element={<ChallengeRoomDetail />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room/create"
                element={<ChallengeRoomCreate />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room/cert/:certId"
                element={<ChallengeRoomCert />}
              />
            )}
            {QA_ENABLED && (
              <Route
                path="/dev/challenge-room/pro-gate"
                element={<ChallengeRoomProGate />}
              />
            )}
            <Route path="/*" element={<DefaultLayout />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
